import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Archive as ArchiveIcon, Plus, Pencil, X, Settings as SettingsIcon } from "lucide-react";
import { plannerApi } from "./plannerApi";
import { toISODate } from "./PlannerCalendar";
import { PLANNER_CATEGORIES, formatAgeRange } from "@/constants/plannerCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const emptyRoom = { name: "", minAgeMonths: 0, maxAgeMonths: 12, capacity: 8, licensedRatio: 4, category: "" };

const RoomFields = ({ value, onChange, testIdPrefix }) => {
  const isYears = value.category === "school_age" || value.category === "after_school";
  return (
  <div className="grid grid-cols-2 gap-2">
    <div className="col-span-2">
      <Label className="text-xs text-slate-500">
        Category
        {value.licensedRatio ? <span className="text-[#4C8C8C] font-medium"> · Ratio 1:{value.licensedRatio}</span> : null}
      </Label>
      <Select
        value={value.category || "__none"}
        onValueChange={(v) => {
          const cat = v === "__none" ? "" : v;
          const label = PLANNER_CATEGORIES.find((c) => c.value === cat)?.label || "";
          const prevLabel = PLANNER_CATEGORIES.find((c) => c.value === value.category)?.label || "";
          // Auto-fill the name only while it still matches the previous category's default
          // label (or is empty) — once the director types a custom name, switching category
          // again won't overwrite it.
          const shouldAutoFill = !value.name || value.name === prevLabel;
          onChange({ ...value, category: cat, name: shouldAutoFill ? label : value.name });
        }}
      >
        <SelectTrigger className="bg-white" data-testid={`${testIdPrefix}-category-select`}><SelectValue placeholder="Not assigned" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">Not assigned</SelectItem>
          {PLANNER_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-slate-400 mt-1">Multiple rooms can share the same category — give each a unique Room Name below.</p>
    </div>
    <div className="col-span-2">
      <Label className="text-xs text-slate-500">Room Name</Label>
      <Input
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        placeholder="e.g. PreK 1"
        data-testid={`${testIdPrefix}-name-input`}
      />
    </div>
    <div><Label className="text-xs text-slate-500">{isYears ? "Min years" : "Min months"}</Label><Input type="number" value={isYears ? Math.round(value.minAgeMonths / 12) : value.minAgeMonths} onChange={(e) => onChange({ ...value, minAgeMonths: isYears ? +e.target.value * 12 : +e.target.value })} data-testid={`${testIdPrefix}-min-age-input`} /></div>
    <div><Label className="text-xs text-slate-500">{isYears ? "Max years" : "Max months"}</Label><Input type="number" value={isYears ? Math.round(value.maxAgeMonths / 12) : value.maxAgeMonths} onChange={(e) => onChange({ ...value, maxAgeMonths: isYears ? +e.target.value * 12 : +e.target.value })} data-testid={`${testIdPrefix}-max-age-input`} /></div>
    <div><Label className="text-xs text-slate-500">Capacity</Label><Input type="number" value={value.capacity} onChange={(e) => onChange({ ...value, capacity: +e.target.value })} data-testid={`${testIdPrefix}-capacity-input`} /></div>
    <div><Label className="text-xs text-slate-500">Ratio 1:X</Label><Input type="number" value={value.licensedRatio} onChange={(e) => onChange({ ...value, licensedRatio: +e.target.value })} data-testid={`${testIdPrefix}-ratio-input`} /></div>
  </div>
  );
};

export const PlannerSettingsDrawer = ({ open, onClose, tz, kgCutoffDate, rooms, onSaved, isDirector, onHeroImagesChanged }) => {
  const [tzValue, setTzValue] = useState(tz);
  const [kgCutoff, setKgCutoff] = useState(kgCutoffDate);
  const [savingKgCutoff, setSavingKgCutoff] = useState(false);
  const [newRoom, setNewRoom] = useState(emptyRoom);
  const [savingTz, setSavingTz] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editRoomForm, setEditRoomForm] = useState(emptyRoom);
  const [archivedRooms, setArchivedRooms] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [children, setChildren] = useState([]);
  const [withdrawalPlans, setWithdrawalPlans] = useState([]);
  const [withdrawalForm, setWithdrawalForm] = useState({ childId: "", plannedWithdrawalDate: "", notes: "" });
  const [heroImages, setHeroImages] = useState([]);
  const [heroUploading, setHeroUploading] = useState(false);
  const [openAdjustId, setOpenAdjustId] = useState(null);
  const heroFileInputRef = useRef(null);
  const today = toISODate(new Date());

  // Re-sync local timezone draft whenever the drawer is (re)opened or the saved value changes —
  // SheetContent stays mounted, so a plain useState initializer would otherwise go stale.
  useEffect(() => {
    if (open) setTzValue(tz);
  }, [open, tz]);

  const loadWithdrawals = async () => {
    try {
      const [c, wp] = await Promise.all([
        axios.get(`${API}/children`, auth()),
        plannerApi.listWithdrawalPlans(),
      ]);
      setChildren(c.data || []);
      setWithdrawalPlans(wp.data || []);
    } catch {
      toast.error("Failed to load withdrawal data");
    }
  };

  useEffect(() => {
    if (open) loadWithdrawals();
  }, [open]);

  const loadHeroImages = async () => {
    try {
      const res = await plannerApi.listHeroImages();
      const metas = res.data || [];
      const withUrls = await Promise.all(metas.map(async (m) => {
        try {
          const blobRes = await plannerApi.getHeroImageBlob(m.id);
          return { ...m, url: URL.createObjectURL(blobRes.data) };
        } catch {
          return null;
        }
      }));
      setHeroImages(withUrls.filter(Boolean));
    } catch {
      toast.error("Failed to load hero photos");
    }
  };

  useEffect(() => {
    if (open) loadHeroImages();
  }, [open]);

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroUploading(true);
    try {
      await plannerApi.uploadHeroImage(file);
      toast.success("Photo uploaded");
      loadHeroImages();
      onHeroImagesChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload photo");
    } finally {
      setHeroUploading(false);
      if (heroFileInputRef.current) heroFileInputRef.current.value = "";
    }
  };

  const deleteHeroImg = async (id) => {
    if (!window.confirm("Remove this hero photo?")) return;
    try {
      await plannerApi.deleteHeroImage(id);
      toast.success("Photo removed");
      loadHeroImages();
      onHeroImagesChanged?.();
    } catch {
      toast.error("Failed to remove photo");
    }
  };

  const patchHeroImg = async (id, field, value) => {
    setHeroImages((imgs) => imgs.map((im) => (im.id === id ? { ...im, [field]: value } : im)));
    try {
      await plannerApi.updateHeroImage(id, { [field]: value });
      onHeroImagesChanged?.();
    } catch {
      toast.error("Failed to save photo setting");
    }
  };

  const createWithdrawalPlan = async () => {
    if (!isDirector) return;
    if (!withdrawalForm.childId || !withdrawalForm.plannedWithdrawalDate) { toast.error("Select a child and a last day"); return; }
    try {
      await plannerApi.createWithdrawalPlan(withdrawalForm);
      toast.success("Withdrawal plan added — it will appear on the Planner calendar");
      setWithdrawalForm({ childId: "", plannedWithdrawalDate: "", notes: "" });
      loadWithdrawals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add withdrawal plan");
    }
  };

  const cancelWithdrawalPlan = async (id) => {
    if (!isDirector) return;
    try { await plannerApi.deleteWithdrawalPlan(id); toast.success("Withdrawal plan cancelled"); loadWithdrawals(); }
    catch { toast.error("Failed to cancel withdrawal plan"); }
  };

  const loadArchivedRooms = async () => {
    try {
      const res = await plannerApi.listRooms(true);
      setArchivedRooms((res.data || []).filter((r) => r.archived));
    } catch {
      toast.error("Failed to load archived rooms");
    }
  };

  const toggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) loadArchivedRooms();
  };

  const saveTz = async () => {
    setSavingTz(true);
    try {
      await plannerApi.updateSettings({ timezone: tzValue, kindergartenCutoffDate: kgCutoff });
      toast.success("Timezone saved");
      onSaved();
    } catch {
      toast.error("Invalid timezone");
    } finally {
      setSavingTz(false);
    }
  };

  const saveKgCutoff = async () => {
    setSavingKgCutoff(true);
    try {
      await plannerApi.updateSettings({ timezone: tzValue, kindergartenCutoffDate: kgCutoff });
      toast.success("Kindergarten cutoff date saved");
      onSaved();
    } catch {
      toast.error("Invalid date — use MM-DD");
    } finally {
      setSavingKgCutoff(false);
    }
  };

  const [addRoomOpen, setAddRoomOpen] = useState(false);

  const addRoom = async () => {
    if (!newRoom.category) { toast.error("Category is required"); return; }
    if (!newRoom.name.trim()) { toast.error("Room Name is required"); return; }
    try {
      await plannerApi.createRoom(newRoom);
      toast.success("Room added");
      setNewRoom(emptyRoom);
      setAddRoomOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add room");
    }
  };

  const startEditRoom = (r) => {
    setEditingRoomId(r.id);
    setEditRoomForm({ name: r.name, minAgeMonths: r.minAgeMonths, maxAgeMonths: r.maxAgeMonths, capacity: r.capacity, licensedRatio: r.licensedRatio, category: r.category || "" });
  };

  const saveEditRoom = async () => {
    if (!editRoomForm.category) { toast.error("Category is required"); return; }
    if (!editRoomForm.name.trim()) { toast.error("Room Name is required"); return; }
    try {
      await plannerApi.updateRoom(editingRoomId, editRoomForm);
      toast.success("Room updated");
      setEditingRoomId(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update room");
    }
  };

  const archiveRoom = async (id) => {
    try { await plannerApi.archiveRoom(id); toast.success("Room archived"); onSaved(); if (showArchived) loadArchivedRooms(); }
    catch { toast.error("Failed to archive room"); }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm("Permanently delete this room?")) return;
    try { await plannerApi.deleteRoom(id); toast.success("Room deleted"); onSaved(); if (showArchived) loadArchivedRooms(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Room is referenced by a tour and cannot be deleted"); }
  };

  const [quickAddOpenFor, setQuickAddOpenFor] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState({ name: "", capacity: 8, licensedRatio: 4 });

  const toggleQuickAdd = (room) => {
    if (quickAddOpenFor === room.id) {
      setQuickAddOpenFor(null);
    } else {
      setQuickAddOpenFor(room.id);
      setQuickAddForm({ name: "", capacity: room.capacity, licensedRatio: room.licensedRatio });
    }
  };

  const submitQuickAdd = async (sourceRoom) => {
    if (!quickAddForm.name.trim()) { toast.error("Room Name is required"); return; }
    try {
      await plannerApi.createRoom({
        name: quickAddForm.name,
        category: sourceRoom.category,
        minAgeMonths: sourceRoom.minAgeMonths,
        maxAgeMonths: sourceRoom.maxAgeMonths,
        capacity: quickAddForm.capacity,
        licensedRatio: quickAddForm.licensedRatio,
      });
      toast.success("Room added");
      setQuickAddOpenFor(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add room");
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid="planner-settings-drawer">
        <SheetHeader>
          <SheetTitle className="text-slate-800">Planner Settings</SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-6">
          <div>
            <Label className="text-xs text-slate-500">Center Timezone</Label>
            <div className="flex gap-2 mt-1">
              <Input value={tzValue} onChange={(e) => setTzValue(e.target.value)} data-testid="planner-tz-input" placeholder="America/New_York" />
              <Button onClick={saveTz} disabled={savingTz} className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" data-testid="planner-tz-save-btn">
                {savingTz ? "…" : "Save"}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Applies to all tour reminders (IANA name, e.g. America/New_York).</p>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Kindergarten Cutoff Date</Label>
            <div className="flex gap-2 mt-1">
              <Input value={kgCutoff} onChange={(e) => setKgCutoff(e.target.value)} data-testid="planner-kg-cutoff-input" placeholder="09-01" />
              <Button onClick={saveKgCutoff} disabled={savingKgCutoff} className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" data-testid="planner-kg-cutoff-save-btn">
                {savingKgCutoff ? "…" : "Save"}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">MM-DD. PreK children who don't turn 5 by this date stay PreK until next year's cutoff.</p>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-1">Hero Banner Photos</p>
            <p className="text-xs text-slate-400 mb-2">
              {isDirector
                ? "Shown at the top of the planner, rotating every few seconds. If none are uploaded, a default photo is shown."
                : "Only a director (owner/co-owner) can manage hero banner photos."}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {heroImages.map((img) => (
                <div key={img.id} className="rounded-md border border-slate-200 overflow-hidden" data-testid={`hero-image-${img.id}`}>
                  <div className="relative h-20 bg-slate-100">
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                    {isDirector && (
                      <div className="absolute top-1 right-1 flex gap-1">
                        <button
                          onClick={() => setOpenAdjustId(openAdjustId === img.id ? null : img.id)}
                          className="bg-white/90 rounded-full p-1 hover:bg-white"
                          title="Adjust"
                          data-testid={`hero-image-adjust-btn-${img.id}`}
                        >
                          <SettingsIcon className="w-3 h-3 text-slate-600" />
                        </button>
                        <button
                          onClick={() => deleteHeroImg(img.id)}
                          className="bg-white/90 rounded-full p-1 hover:bg-white"
                          title="Remove"
                          data-testid={`hero-image-delete-${img.id}`}
                        >
                          <X className="w-3 h-3 text-[#C97B7B]" />
                        </button>
                      </div>
                    )}
                  </div>
                  {openAdjustId === img.id && (
                    <div className="p-2 space-y-1.5 bg-slate-50" data-testid={`hero-image-adjust-panel-${img.id}`}>
                      <Select value={img.fit} onValueChange={(v) => patchHeroImg(img.id, "fit", v)}>
                        <SelectTrigger className="h-7 text-xs bg-white" data-testid={`hero-image-fit-${img.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cover">Fit: Cover</SelectItem>
                          <SelectItem value="contain">Fit: Contain</SelectItem>
                          <SelectItem value="stretch">Fit: Stretch</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={img.hPos} onValueChange={(v) => patchHeroImg(img.id, "hPos", v)}>
                        <SelectTrigger className="h-7 text-xs bg-white" data-testid={`hero-image-hpos-${img.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">Horizontal: Left</SelectItem>
                          <SelectItem value="center">Horizontal: Center</SelectItem>
                          <SelectItem value="right">Horizontal: Right</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={img.vPos} onValueChange={(v) => patchHeroImg(img.id, "vPos", v)}>
                        <SelectTrigger className="h-7 text-xs bg-white" data-testid={`hero-image-vpos-${img.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top">Vertical: Top</SelectItem>
                          <SelectItem value="center">Vertical: Center</SelectItem>
                          <SelectItem value="bottom">Vertical: Bottom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {heroImages.length === 0 && <p className="text-xs text-slate-300 mt-1">No hero photos uploaded yet — the default photo is shown.</p>}
            {isDirector && (
              <>
                <input
                  ref={heroFileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleHeroUpload}
                  data-testid="hero-image-file-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => heroFileInputRef.current?.click()}
                  disabled={heroUploading}
                  data-testid="hero-image-upload-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> {heroUploading ? "Uploading…" : "Upload Photo"}
                </Button>
              </>
            )}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Rooms</p>
            <div className="space-y-2">
              {rooms.map((r) => (
                <div key={r.id} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`room-row-${r.id}`}>
                  {editingRoomId === r.id ? (
                    <div className="space-y-2">
                      <RoomFields value={editRoomForm} onChange={setEditRoomForm} testIdPrefix="room-edit" />
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" onClick={saveEditRoom} data-testid={`room-edit-save-${r.id}`}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingRoomId(null)} data-testid={`room-edit-cancel-${r.id}`}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-slate-600">
                          <span className="font-medium text-slate-700">{r.name}</span>
                          <span className="text-slate-400"> · {formatAgeRange(r.minAgeMonths, r.maxAgeMonths, r.category)} · cap {r.capacity} · 1:{r.licensedRatio}</span>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => startEditRoom(r)} title="Edit" data-testid={`room-edit-${r.id}`}><Pencil className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" /></button>
                          <button onClick={() => archiveRoom(r.id)} title="Archive" data-testid={`room-archive-${r.id}`}><ArchiveIcon className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" /></button>
                          <button onClick={() => deleteRoom(r.id)} title="Delete permanently" data-testid={`room-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-[#C97B7B]" /></button>
                        </div>
                      </div>
                      {r.category && (
                        <div className="mt-1.5">
                          <button
                            type="button"
                            onClick={() => toggleQuickAdd(r)}
                            className="text-xs text-[#4C8C8C] hover:text-[#3f7575] font-medium"
                            data-testid={`quick-add-room-btn-${r.id}`}
                          >
                            + Add another {PLANNER_CATEGORIES.find((c) => c.value === r.category)?.label || r.name} room
                          </button>
                          <p className="text-[11px] text-slate-400">Use this only if this category has more than one physical room.</p>
                          {quickAddOpenFor === r.id && (
                            <div className="mt-2 grid grid-cols-2 gap-2 bg-slate-50 rounded-md p-2" data-testid={`quick-add-form-${r.id}`}>
                              <div className="col-span-2">
                                <Label className="text-xs text-slate-500">Room Name</Label>
                                <Input
                                  value={quickAddForm.name}
                                  onChange={(e) => setQuickAddForm({ ...quickAddForm, name: e.target.value })}
                                  placeholder={`e.g. ${r.name} 2`}
                                  data-testid={`quick-add-name-${r.id}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Capacity</Label>
                                <Input
                                  type="number"
                                  value={quickAddForm.capacity}
                                  onChange={(e) => setQuickAddForm({ ...quickAddForm, capacity: +e.target.value })}
                                  data-testid={`quick-add-capacity-${r.id}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Ratio 1:X</Label>
                                <Input
                                  type="number"
                                  value={quickAddForm.licensedRatio}
                                  onChange={(e) => setQuickAddForm({ ...quickAddForm, licensedRatio: +e.target.value })}
                                  data-testid={`quick-add-ratio-${r.id}`}
                                />
                              </div>
                              <div className="col-span-2 flex gap-2">
                                <Button size="sm" className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" onClick={() => submitQuickAdd(r)} data-testid={`quick-add-save-${r.id}`}>Save</Button>
                                <Button size="sm" variant="outline" onClick={() => setQuickAddOpenFor(null)} data-testid={`quick-add-cancel-${r.id}`}>Cancel</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {rooms.length === 0 && <p className="text-sm text-slate-300">No rooms yet.</p>}
            </div>

            <div className="mt-3">
              {!addRoomOpen ? (
                <button
                  type="button"
                  onClick={() => setAddRoomOpen(true)}
                  className="text-xs text-[#4C8C8C] hover:text-[#3f7575] font-medium"
                  data-testid="open-add-room-btn"
                >
                  + Add Room
                </button>
              ) : (
                <div className="rounded-md border border-dashed border-slate-300 p-3 space-y-2">
                  <RoomFields value={newRoom} onChange={setNewRoom} testIdPrefix="room" />
                  <div className="flex gap-2">
                    <Button onClick={addRoom} variant="outline" className="flex-1" data-testid="add-room-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Room</Button>
                    <Button variant="ghost" onClick={() => { setAddRoomOpen(false); setNewRoom(emptyRoom); }} data-testid="cancel-add-room-btn">Cancel</Button>
                  </div>
                </div>
              )}
            </div>

            <button onClick={toggleArchived} className="text-xs text-slate-400 hover:text-slate-600 mt-3 underline" data-testid="toggle-archived-rooms-btn">
              {showArchived ? "Hide" : "Show"} Archived Rooms
            </button>
            {showArchived && (
              <div className="mt-2 space-y-2" data-testid="archived-rooms-list">
                {archivedRooms.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`archived-room-row-${r.id}`}>
                    <div className="text-sm text-slate-500">
                      <span className="font-medium">{r.name}</span>
                      <span className="text-slate-400"> · {formatAgeRange(r.minAgeMonths, r.maxAgeMonths, r.category)} · cap {r.capacity} · 1:{r.licensedRatio}</span>
                    </div>
                    <button onClick={() => deleteRoom(r.id)} title="Delete permanently" data-testid={`archived-room-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-[#C97B7B]" /></button>
                  </div>
                ))}
                {archivedRooms.length === 0 && <p className="text-xs text-slate-300">No archived rooms.</p>}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-1">Planned Withdrawals</p>
            <p className="text-xs text-slate-400 mb-2">
              {isDirector
                ? "A child counts through their last day. Also appears on the Planner calendar. Only one active plan per child."
                : "A child counts through their last day. Only a director (owner/co-owner) can add or cancel a plan."}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Select value={withdrawalForm.childId} onValueChange={(v) => setWithdrawalForm({ ...withdrawalForm, childId: v })} disabled={!isDirector}>
                <SelectTrigger className="bg-white" data-testid="withdrawal-child-select"><SelectValue placeholder="Child" /></SelectTrigger>
                <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <div>
                <Label className="text-xs text-slate-500">Last Day</Label>
                <Input
                  type="date"
                  min={today}
                  value={withdrawalForm.plannedWithdrawalDate}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, plannedWithdrawalDate: e.target.value })}
                  disabled={!isDirector}
                  data-testid="withdrawal-date-input"
                />
              </div>
            </div>
            <Button
              size="sm"
              className="mt-2 bg-[#4C8C8C] hover:bg-[#3f7575] text-white"
              onClick={createWithdrawalPlan}
              disabled={!isDirector}
              data-testid="add-withdrawal-plan-btn"
            >
              Add Withdrawal Plan
            </Button>

            <div className="mt-3 space-y-1.5">
              {withdrawalPlans.map((w) => (
                <div key={w.id} className="flex items-center justify-between rounded-md border border-[#C97B7B]/20 bg-[#C97B7B]/[0.04] px-3 py-1.5" data-testid={`withdrawal-plan-row-${w.id}`}>
                  <span className="text-xs text-slate-500">{w.childName} — Last Day: {w.plannedWithdrawalDate}</span>
                  {isDirector && (
                    <button onClick={() => cancelWithdrawalPlan(w.id)} data-testid={`cancel-withdrawal-plan-${w.id}`}><Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-[#C97B7B]" /></button>
                  )}
                </div>
              ))}
              {withdrawalPlans.length === 0 && <p className="text-xs text-slate-300">No planned withdrawals.</p>}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
